/**
 * ESS Climate Intelligence — NOAA Data Layer
 * Fetches and parses RONI, Niño 3.4, and ENSO probability data from NOAA CPC.
 * Uses Vite dev proxy for CORS. Production needs an ESS backend cache layer.
 */

// ---------------------------------------------------------------------------
// RONI + Niño 3.4 — from NOAA CPC RONI.ascii.txt
// ---------------------------------------------------------------------------

let _ronicache = null;

export async function fetchRONIData() {
  if (_ronicache) return _ronicache;

  try {
    const res = await fetch('/api/enso/history?years=60'); // Fetch full history
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    
    // Map backend format to what chart expects
    const records = data.map(r => ({
      year: r.year,
      month: r.month,
      nino34: r.nino34,
      roni: r.roni,
      date: new Date(r.year, r.month - 1, 1),
      label: r.label,
    }));

    _ronicache = records;
    return records;
  } catch (err) {
    console.error('[NOAA] RONI fetch failed:', err.message);
    throw err;
  }
}

export async function fetchCurrentStatus() {
  try {
    const res = await fetch('/api/enso/current');
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    
    let statusClass = 'neutral';
    if (data.status.includes('El Niño')) statusClass = 'el-nino';
    else if (data.status.includes('La Niña')) statusClass = 'la-nina';
    
    // RONI (a 3-month season) and Niño 3.4 (a calendar month) publish on
    // their own independent cadences, so the backend now reports each
    // metric's own label rather than forcing one shared timestamp onto
    // both — parse each separately instead of assuming they match.
    const parseLabel = (label) => {
      if (!label) return null;
      const [monthStr, yearStr] = label.split(' ');
      const monthIdx = MONTH_NAMES.indexOf(monthStr);
      const year = parseInt(yearStr || new Date().getFullYear());
      return monthIdx >= 0 ? new Date(year, monthIdx, 1) : null;
    };

    const roniLabel = data.roniLabel ?? data.label;
    const nino34Label = data.nino34Label ?? data.label;

    return {
      status: data.status,
      statusClass,
      roni: data.roni !== null ? data.roni.toFixed(2) : 'N/A',
      nino34: data.nino34 !== null ? data.nino34.toFixed(2) : 'N/A',
      nino34Sst: data.nino34Sst !== null && data.nino34Sst !== undefined ? data.nino34Sst.toFixed(2) : null,
      nino34Normal: data.nino34Normal !== null && data.nino34Normal !== undefined ? data.nino34Normal.toFixed(2) : null,
      updated: roniLabel,
      updatedDate: parseLabel(roniLabel) || new Date(),
      nino34Updated: nino34Label,
      nino34UpdatedDate: parseLabel(nino34Label),
      alertStatus: data.alertStatus ?? null,
      synopsis: data.synopsis ?? null,
      bulletinIssued: data.bulletinIssued ?? null,
    };
  } catch (e) {
    console.error('[NOAA] Current status fetch failed:', e.message);
    throw e;
  }
}

// ---------------------------------------------------------------------------
// ENSO outlook — NOAA CPC's official monthly tables (probabilities, strength chances, RONI forecast
// percentiles) plus the "historic event" chance quoted in the bulletin. There is NO fallback data:
// when NOAA's tables are not available the functions return null and the UI says so.
// ---------------------------------------------------------------------------

export async function fetchENSOOutlook() {
  try {
    const res = await fetch('/api/enso/outlook');
    if (res.status === 404) return null;           // not published / not parsed yet — never substitute numbers
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    console.error('[NOAA] ENSO outlook fetch failed:', err.message);
    return null;
  }
}

export async function fetchENSOProbabilities() {
  const outlook = await fetchENSOOutlook();
  return outlook ? outlook.seasons : null;
}

const SEASON_LONG = { DJF: 'Dec–Feb', JFM: 'Jan–Mar', FMA: 'Feb–Apr', MAM: 'Mar–May', AMJ: 'Apr–Jun', MJJ: 'May–Jul', JJA: 'Jun–Aug', JAS: 'Jul–Sep', ASO: 'Aug–Oct', SON: 'Sep–Nov', OND: 'Oct–Dec', NDJ: 'Nov–Jan' };
/** "OND 2026" → "Oct–Dec 2026" */
export function seasonName(label) { const [a, y] = label.split(' '); return `${SEASON_LONG[a] || a} ${y}`; }
/** "2026-10-08" → "8 October 2026" */
export function longDate(iso) { const [y, m, d] = iso.split('-').map(Number); return `${d} ${['January','February','March','April','May','June','July','August','September','October','November','December'][m - 1]} ${y}`; }

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

export const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export function filterByYears(data, years) {
  if (years === 'all') return data;
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - years);
  return data.filter(d => d.date >= cutoff);
}
